import { useCallback, useEffect, useState } from 'react';

import { SUNBIT_BRANDED_CREDIT_CARD_STORAGE_KEY } from '~/pdp/components/SunbitModal/constants';

interface SunbitStoreState {
  hasShownModal: boolean;
  setHasShownModal: (value: boolean) => void;
  resetStore: () => void;
}

function getInitialState(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const stored = localStorage.getItem(SUNBIT_BRANDED_CREDIT_CARD_STORAGE_KEY);
    return stored === 'true';
  } catch {
    return false;
  }
}

/**
 * Sunbit store hook — persisted to localStorage.
 *
 * `hasShownModal` survives page refreshes but should reset on logout
 * (via resetStore), so the modal shows once per "login lifetime."
 */
export function useSunbitStore(): SunbitStoreState {
  const [hasShownModal, setHasShownModalState] = useState<boolean>(false);

  useEffect(() => {
    setHasShownModalState(getInitialState());
  }, []);

  const setHasShownModal = useCallback((value: boolean) => {
    setHasShownModalState(value);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(
          SUNBIT_BRANDED_CREDIT_CARD_STORAGE_KEY,
          String(value)
        );
      } catch {
        // Silently fail if localStorage is not available
      }
    }
  }, []);

  const resetStore = useCallback(() => {
    setHasShownModalState(false);
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(SUNBIT_BRANDED_CREDIT_CARD_STORAGE_KEY);
      } catch {
        // Silently fail if localStorage is not available
      }
    }
  }, []);

  return {
    hasShownModal,
    setHasShownModal,
    resetStore,
  };
}

export function resetSunbitStore(): void {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(SUNBIT_BRANDED_CREDIT_CARD_STORAGE_KEY);
    } catch {
      // Silently fail if localStorage is not available
    }
  }
}

export default useSunbitStore;
